import { defineStore } from 'pinia'
import { getBrand, quoteMinQuantity } from '~~/app/brands'
import {
  QUOTE_LIST_STORAGE_KEY,
  addQuoteListItem,
  quoteListBelowMinimum,
  quoteListTotalQuantity,
  readQuoteList,
  removeQuoteListItem,
  serializeQuoteList,
  setQuoteListQuantity,
  toQuotePayloadItems,
  type QuoteListItem,
  type QuoteListItemInput,
  type QuoteListPayloadItem,
} from '~~/shared/utils/quoteList'

/**
 * Lista Zapytania — stan wybranych produktów przed wysłaniem zapytania o wycenę.
 *
 * Świadomie NIE jest koszykiem: nic nie leci na serwer przy dodawaniu pozycji.
 * Sklep katalogowy odpowiada 403 na wszystkich ścieżkach zakupowych i ma tak
 * zostać, więc lista żyje wyłącznie w przeglądarce, a backend widzi ją dopiero
 * jako tablicę pozycji w POST /rest/quote-request.
 *
 * Reguły (limit, sumowanie duplikatów, walidacja nakładu) siedzą w
 * shared/utils/quoteList — tutaj zostaje stan, trwałość i obsługa błędów.
 *
 * Pozycję wskazuje para (produkt, wariant). Drugi argument metod
 * (`idProductAttribute`) jest opcjonalny i domyślnie 0 — wywołania sprzed
 * obsługi wariantów działają bez zmian.
 */
export const useQuoteListStore = defineStore('quoteList', () => {
  const items = ref<QuoteListItem[]>([])
  const lastError = ref<string | null>(null)
  const hydrated = ref(false)

  // Obiekt konfiguracji łapiemy przy tworzeniu store, a markę czytamy przy
  // każdym wywołaniu — funkcja, nie computed: konfiguracja runtime nie jest
  // reaktywna, a store żyje dłużej niż widok, który go utworzył.
  const runtimeConfig = useRuntimeConfig()

  /** Minimalny nakład marki (BrandConfig.quoteMinQuantity); 1 = bez minimum. */
  function minQuantity(): number {
    return quoteMinQuantity(getBrand(runtimeConfig.public.brand as string | undefined))
  }

  const count = computed(() => items.value.length)
  const totalQuantity = computed(() => quoteListTotalQuantity(items.value))
  const isEmpty = computed(() => items.value.length === 0)

  /** Pozycje z nakładem poniżej minimum — np. zebrane, zanim minimum weszło. */
  function belowMinimum(): QuoteListItem[] {
    return quoteListBelowMinimum(items.value, { minQuantity: minQuantity() })
  }

  function find(idProduct: number, idProductAttribute = 0): QuoteListItem | undefined {
    return items.value.find(entry => entry.id_product === idProduct && entry.id_product_attribute === idProductAttribute)
  }

  function contains(idProduct: number, idProductAttribute = 0): boolean {
    return find(idProduct, idProductAttribute) !== undefined
  }

  function quantityOf(idProduct: number, idProductAttribute = 0): number {
    return find(idProduct, idProductAttribute)?.quantity ?? 0
  }

  /**
   * Wczytuje listę zapisaną w przeglądarce.
   *
   * Na serwerze lista jest ZAWSZE pusta — localStorage nie istnieje w SSR.
   * Dlatego licznik w nagłówku musi tolerować rozjazd: serwer wyrenderuje zero,
   * a właściwa liczba pojawi się po hydracji. Renderowanie licznika bez tego
   * założenia daje ostrzeżenie o niezgodności hydracji.
   */
  function hydrate(): void {
    if (!import.meta.client || hydrated.value) {
      return
    }

    try {
      items.value = readQuoteList(window.localStorage.getItem(QUOTE_LIST_STORAGE_KEY))
    }
    catch {
      // Tryb prywatny części przeglądarek rzuca już przy samym dostępie do
      // localStorage. Pusta lista jest wtedy poprawnym stanem, nie błędem.
      items.value = []
    }

    hydrated.value = true
  }

  function persist(): void {
    if (!import.meta.client) {
      return
    }

    try {
      window.localStorage.setItem(QUOTE_LIST_STORAGE_KEY, serializeQuoteList(items.value))
    }
    catch {
      // Zapełniony albo zablokowany storage nie może wywrócić dodawania pozycji:
      // lista zadziała w obrębie tej karty, tylko nie przeżyje odświeżenia.
    }
  }

  /** @returns czy pozycja trafiła na listę; przy false powód jest w lastError */
  function add(item: QuoteListItemInput): boolean {
    return apply(addQuoteListItem(items.value, item, { minQuantity: minQuantity() }))
  }

  function remove(idProduct: number, idProductAttribute = 0): void {
    items.value = removeQuoteListItem(items.value, idProduct, idProductAttribute)
    lastError.value = null
    persist()
  }

  /** @returns czy nakład został zmieniony; przy false powód jest w lastError */
  function setQuantity(idProduct: number, quantity: unknown, idProductAttribute = 0): boolean {
    return apply(setQuoteListQuantity(items.value, idProduct, quantity, {
      idProductAttribute,
      minQuantity: minQuantity(),
    }))
  }

  function apply(result: { items: QuoteListItem[], error: string | null }): boolean {
    lastError.value = result.error

    if (result.error !== null) {
      return false
    }

    items.value = result.items
    persist()

    return true
  }

  function clear(): void {
    items.value = []
    lastError.value = null
    persist()
  }

  function toPayload(): QuoteListPayloadItem[] {
    return toQuotePayloadItems(items.value)
  }

  return {
    items,
    lastError,
    hydrated,
    minQuantity,
    count,
    totalQuantity,
    isEmpty,
    belowMinimum,
    contains,
    quantityOf,
    hydrate,
    add,
    remove,
    setQuantity,
    clear,
    toPayload,
  }
})
